import { Wallet, Clock, CheckCircle2, Car, CalendarCheck, Activity } from "lucide-react";
import { useTranslation } from "react-i18next";
import { money } from "@/lib/format";

const Stat = ({ icon: Icon, label, value, tone, testid }) => (
  <div data-testid={testid} className="bg-white rounded-2xl shadow-card border border-slate-100 p-6">
    <span className={`grid place-items-center w-11 h-11 rounded-xl ${tone}`}><Icon size={20} /></span>
    <p className="font-heading text-2xl font-bold text-slate-900 mt-4">{value}</p>
    <p className="text-sm text-slate-500 mt-1">{label}</p>
  </div>
);

const TripStat = ({ icon: Icon, label, value }) => (
  <div className="rounded-xl bg-slate-50 py-4">
    <Icon size={18} className="mx-auto text-blue-600" />
    <p className="font-heading text-xl font-bold text-slate-900 mt-2">{value}</p>
    <p className="text-xs text-slate-500 mt-0.5">{label}</p>
  </div>
);

export function HostOverview({ summary, loading }) {
  const { t } = useTranslation();
  if (loading || !summary) return <p className="text-slate-400" data-testid="host-overview-loading">{t("hostOverview.loading")}</p>;
  const { earnings, trips, listings, commission_rate } = summary;
  return (
    <div className="space-y-4" data-testid="host-overview">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Stat testid="stat-ready" icon={Wallet} label={t("hostOverview.readyToPayOut")} value={money(earnings.ready)} tone="bg-green-50 text-green-600" />
        <Stat testid="stat-pending" icon={Clock} label={t("hostOverview.pending")} value={money(earnings.pending)} tone="bg-amber-50 text-amber-600" />
        <Stat testid="stat-paid" icon={CheckCircle2} label={t("hostOverview.paidOut")} value={money(earnings.paid)} tone="bg-blue-50 text-blue-600" />
        <Stat testid="stat-listings" icon={Car} label={t("hostOverview.approvedTotal")} value={`${listings.approved}/${listings.total}`} tone="bg-slate-100 text-slate-600" />
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="bg-white rounded-2xl shadow-card border border-slate-100 p-6">
          <h3 className="font-heading font-semibold text-slate-900 mb-4">{t("hostOverview.trips")}</h3>
          <div className="grid grid-cols-3 gap-3 text-center">
            <TripStat icon={CalendarCheck} label={t("hostOverview.upcoming")} value={trips.upcoming} />
            <TripStat icon={Activity} label={t("hostOverview.activeNow")} value={trips.active} />
            <TripStat icon={Car} label={t("hostOverview.allTime")} value={trips.total} />
          </div>
        </div>
        <div className="bg-white rounded-2xl shadow-card border border-slate-100 p-6">
          <h3 className="font-heading font-semibold text-slate-900 mb-4">Listings</h3>
          <div className="flex flex-wrap gap-2 text-xs">
            <span className="px-3 py-1 rounded-full bg-emerald-50 text-emerald-600 font-medium">{listings.approved} approved</span>
            <span className="px-3 py-1 rounded-full bg-amber-50 text-amber-600 font-medium">{t("hostOverview.underReview", { count: listings.under_review })}</span>
            <span className="px-3 py-1 rounded-full bg-rose-50 text-rose-600 font-medium">{t("hostOverview.rejected", { count: listings.rejected })}</span>
          </div>
        </div>
      </div>
    </div>
  );
}
